import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { useGetCustomerById } from '@/hooks/useGetCustomerById';
import { useUpdateCustomer } from '@/hooks/useUpdateCustomer';
import { Status } from '@/services/CustomerService';

const updateCustomerSchema = z.object({
  id: z.number().min(1, 'Customer ID is required'),
  name: z.string().min(1, 'Name is required'),
  freemium: z.boolean(),
  status: z.nativeEnum(Status),
  group: z.string().optional(),
});

type UpdateCustomerFormData = z.infer<typeof updateCustomerSchema>;

interface EditCustomerFormProps {
  customerId: number;
  onSuccess: (value: boolean) => void;
  onCancel: () => void;
}

export default function EditCustomerForm({
  customerId,
  onSuccess,
  onCancel,
}: EditCustomerFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateCustomerFormData>({
    resolver: zodResolver(updateCustomerSchema),
  });

  const { customer, customerLoading } = useGetCustomerById(customerId);

  const { updateCustomer, isLoading } = useUpdateCustomer();

  async function onSubmit(data: UpdateCustomerFormData) {
    try {
      await updateCustomer(data);

      toast.success('Customer updated successfully!');
      onSuccess(true);
    } catch (error) {
      toast.error(
        error instanceof AxiosError
          ? error.response?.data?.message
          : 'Failed to update customer. Please try again.',
      );
    }
  }

  useEffect(() => {
    if (customer) {
      reset({
        id: customer.id,
        name: customer.name,
        group: customer.group,
        freemium: customer.freemium,
        status: customer.status ?? Status.INACTIVE,
      });
    }
  }, [customer, reset]);

  if (customerLoading) {
    return (
      <Card className="mx-auto w-full max-w-2xl">
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Loading customer data...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mx-auto w-full max-w-2xl">
      <CardHeader>
        <CardTitle>Edit Customer</CardTitle>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormGroup error={{ message: errors.name?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="name">Name</RequiredLabel>
            <Input id="name" {...register('name')} />
          </FormGroup>

          <FormGroup error={{ message: errors.group?.message, font: 'sm' }}>
            <Label htmlFor="group">Group</Label>
            <Input id="group" {...register('group')} />
          </FormGroup>

          <FormGroup error={{ message: errors.status?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="status">Status</RequiredLabel>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ?? Status.ACTIVE}
                  onValueChange={(value) => field.onChange(value as Status)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={Status.ACTIVE}>Active</SelectItem>
                    <SelectItem value={Status.INACTIVE}>Inactive</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </FormGroup>

          <FormGroup className="flex items-center space-x-2">
            <Controller
              name="freemium"
              control={control}
              render={({ field }) => (
                <Switch
                  id="freemium"
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label htmlFor="freemium">Freemium</Label>
          </FormGroup>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={isLoading || customerLoading}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              className="flex-1"
              disabled={isLoading || customerLoading}
            >
              {(isLoading || customerLoading) && (
                <Loader2 className="mr-2 animate-spin" />
              )}
              {!isLoading && !customerLoading && 'Update Customer'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
